import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../store/AuthContext";
import { getTopics } from "../../api/topicsApi";  // <-- You will create this API function
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function StudentSelectTopicSSO() {
  const { user, token } = useContext(AuthContext);

  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const theme = useTheme();

  const classId = params.get("class_id") || user.class_id;
  const subjectId = params.get("subject_id");

  // Responsive detection
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isTV = useMediaQuery("(min-width: 1920px)");

  // Detect Android App / TV
  const isAndroidApp = () =>
    navigator.userAgent.includes("VrPlayApp") ||
    navigator.userAgent.includes("AndroidWebView") ||
    window.AndroidWebView !== undefined;

  const isAndroidTV = () =>
    navigator.userAgent.includes("AndroidTV") ||
    navigator.userAgent.includes("TV") ||
    isAndroidApp();

  // Navigate to subjects page
  const goToSubjects = () => {
    navigate(`/student/select-subject-sso?class_id=${classId}`, { replace: true });
  };

  useEffect(() => {
    loadTopics();
  }, [classId, subjectId]);

  const loadTopics = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await getTopics(token);  
      const all = res.data;
      console.log(all);
      

      // Filter: class + subject
      const filtered = all.filter(
        (t) => t.subject_id == subjectId
      );
console.log(filtered);

      setTopics(filtered);
    } catch (err) {
      console.error(err);
      setError("Failed to load topics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Auto focus first card on TV
  useEffect(() => {
    if (!loading && topics.length > 0 && isAndroidTV()) {
      setTimeout(() => {
        const firstCard = document.querySelector(".tv-card");
        if (firstCard) firstCard.focus();
      }, 120);
    }
  }, [loading, topics]);

  // DPAD Navigation
  useEffect(() => {
    if (!isAndroidTV()) return;

    const handleDPAD = (e) => {
      const cards = Array.from(document.querySelectorAll(".tv-card"));
      if (cards.length === 0) return;

      const currentIndex = cards.indexOf(document.activeElement);
      const columns = isTV ? 4 : isDesktop ? 3 : isTablet ? 2 : 1;
      let nextIndex = currentIndex;

      switch (e.key) {
        case "ArrowRight":
        case "Right":
          nextIndex = currentIndex + 1;
          break;

        case "ArrowLeft":
        case "Left":
          nextIndex = currentIndex - 1;
          break;

        case "ArrowDown":
        case "Down":
          nextIndex = currentIndex + columns;
          break;

        case "ArrowUp":
        case "Up":
          nextIndex = currentIndex - columns;
          break;

        case "Enter":
        case "NumpadEnter":
        case " ":
          document.activeElement?.click();
          e.preventDefault();
          return;

        default:
          return;
      }

      if (nextIndex >= 0 && nextIndex < cards.length) {
        cards[nextIndex].focus();
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleDPAD);
    return () => window.removeEventListener("keydown", handleDPAD);
  }, [topics, isTV, isDesktop, isTablet]);

  // Card styling — same as subjects
  const getCardStyles = () => {
    const base = {
      cursor: "pointer",
      overflow: "hidden",
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
      }
    };

    if (isTV) {
      return {
        ...base,
        height: 260,
        borderRadius: 3,
        transition: "all 0.2s ease",
        "&:focus": {
          transform: "scale(1.08)",
          boxShadow: `0 0 30px ${theme.palette.primary.main}`,
          border: `3px solid ${theme.palette.primary.main}`,
          outline: "none"
        }
      };
    }

    if (isDesktop) {
      return {
        ...base,
        height: 200,
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[6]
        }
      };
    }

    if (isTablet) {
      return {
        ...base,
        height: 170
      };
    }

    return {
      ...base,
      height: 140
    };
  };

  const getGridConfig = () => {
    if (isTV) return { xs: 12, sm: 6, md: 4, lg: 3 };
    if (isDesktop) return { xs: 12, sm: 6, md: 4 };
    if (isTablet) return { xs: 12, sm: 6 };
    return { xs: 12 };
  };

  const openVideos = (topicId) => {
    navigate(
      `/student/videos?class_id=${classId}&subject_id=${subjectId}&topic_id=${topicId}`
    );
  };

  // Loading UI
  if (loading) {
    return (
      <Box sx={{ 
        background: "#000", 
        minHeight: "100vh", 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        flexDirection: "column" 
      }}>
        <CircularProgress size={isTV ? 80 : 50} />
        <Typography mt={2} color="#ccc">
          Loading Topics...
        </Typography>
      </Box>
    );
  }

  // Error UI
  if (error) {
    return (
      <Box sx={{ background: "#000", minHeight: "100vh", py: 4 }}>
        <Container maxWidth="md">
          <Alert
            severity="error"
            action={<Button onClick={loadTopics}>Retry</Button>}
          >
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ background: "#000", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        {/* HEADER WITH BACK BUTTON */}
        <Box sx={{ mb: 4, position: "relative" }}>
          {/* Back to Subjects Button - Top Left (Hidden on mobile/tablet, shown on desktop) */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goToSubjects}
            variant="outlined"
            sx={{
              display: { xs: "none", md: "flex" }, // Hidden on xs/sm/md, shown on lg+
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#4f8ef7",
              borderColor: "#4f8ef7",
              "&:hover": {
                borderColor: "#d24df2",
                backgroundColor: "rgba(79, 142, 247, 0.1)"
              }
            }}
          >
            Subjects
          </Button>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                mb: 1,
                background: "linear-gradient(45deg, #4f8ef7, #d24df2)",
                WebkitBackgroundClip: "text",
                color: "transparent",
                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" }
              }}
            >
              Select Topic
            </Typography>

            <Typography 
              variant="body1" 
              color="#bbb"
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem" }
              }}
            >
              Choose a topic to view available video lessons
            </Typography>
          </Box>
        </Box>

        {/* TOPICS GRID */}
        <Grid container spacing={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          {topics.map((topic) => (
            <Grid item xs={12} sm={6} md={4} key={topic.id}>
              <Card
                onClick={() => openVideos(topic.id)}
                sx={{
                  cursor: "pointer",
                  height: 220,
                  width: 200,
                  background: "#111",
                  border: "1px solid #222",
                  borderRadius: "10px",
                  transition: "0.2s",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  "&:hover": {
                    transform: "scale(1.03)",
                    boxShadow: "0 0 12px rgba(255, 255, 255, 1)"
                  }
                }}
              >
                <CardContent
                  sx={{
                    textAlign: "center"
                  }}
                >
                  {/* Subject Icon Circle */}
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: "50%",
                      mb: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#fff",
                      fontSize: "1.7rem",
                      fontWeight: "bold"
                    }}
                  >
                    {topic.topic_name?.charAt(0) || "T"}
                  </Box>

                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="#fff"
                  >
                    {topic.topic_name}
                  </Typography>

                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Subjects Button - Bottom Center (for mobile/tablet) */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            variant="contained"
            onClick={goToSubjects}
            sx={{
              background: "linear-gradient(45deg, #4f8ef7, #d24df2)",
              color: "#fff",
              padding: "10px 30px",
              fontWeight: "bold",
              fontSize: "1rem",
              "&:hover": {
                background: "linear-gradient(45deg, #3a7cf0, #c13ce5)",
                transform: "translateY(-2px)",
                boxShadow: "0 5px 15px rgba(79, 142, 247, 0.4)"
              }
            }}
          >
            ← Back to Subjects
          </Button>
        </Box>

        {/* EMPTY STATE */}
        {topics.length === 0 && (
          <Box textAlign="center" py={6}>
            <Typography variant="h6" color="#ccc">
              No topics found
            </Typography>
            <Typography variant="body2" color="#777">
              Please try again later.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}